import React, {type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState} from 'react'
import axios from 'axios'
import {route} from 'ziggy-js'
import {SparklesIcon} from '@heroicons/react/24/solid'
import GeneralButton from '@/Components/Buttons/GeneralButton'
import PusherService from '@/Library/Services/PusherService'
import EventAssistantApproval from '@/Pages/Events/Assistant/EventAssistantApproval'
import LinkedText from '@/Pages/Events/Assistant/LinkedText'
import EventAssistantRecent, {iconButtonClass} from '@/Pages/Events/Assistant/EventAssistantRecent'
import {
    applyAssistantEvents,
    approvalDecisions,
    decisionLine,
    attachmentStatusText,
    COACH_FILE_ACCEPT,
    coachFileRefusal,
    composerDrop,
    emptyAnswer,
    figureReport,
    fileTypeBadge,
    moreRowsLabel,
    previewCount,
    purchaseReplyParts,
    previewRows,
    showsTranscriptBubble,
    storedThread,
    type AssistantApproval,
    type AssistantStreamEvent,
    type AssistantToolLine,
    type FigureReport,
    type PurchaseChoice,
    type StoredThreadMessage,
    type TicketNextStep,
    type TicketNextSteps,
} from '@/Pages/Events/Assistant/assistantTranscript'
import ArrowUpIcon from '@images/arrow-up.svg?react'
import PaperclipIcon from '@images/paperclip.svg?react'
import XMarkIcon from '@images/x-mark.svg?react'

const starterQuestions = [
    {id: 'coach-passes', label: 'Upload coach passes'},
    {id: 'tickets-sold', label: 'Total tickets sold'},
    {id: 'resend', label: 'Re-send a ticket/pass'},
    {id: 'last-week', label: 'How much did we make last week?'},
] as const

interface Props {
    eventId: string | null
    subject?: string
    page: string
    placement?: 'event' | 'shell'
    concealed?: boolean
    onClose: () => void
}

interface ChatLine {
    id: string
    role: 'user' | 'assistant'
    text: string
    thoughts: string
    tools: AssistantToolLine[]
    purchases: PurchaseChoice[]
    nextSteps: TicketNextSteps | null
    approvals: AssistantApproval[]
    failed: string | null
    done: boolean
    attachmentName: string | null
    rows: string[][]
    remaining: number
    capped: boolean
}

const keyed = (values: string[]): Array<{key: string, value: string}> => {
    const counts = new Map<string, number>()

    return values.map((value) => {
        const count = counts.get(value) ?? 0
        counts.set(value, count + 1)

        return {key: `${count}:${value}`, value}
    })
}

const FilePreview = ({
    rows,
    label,
    remaining,
    capped,
}: {
    rows: string[][]
    label: string
    remaining: number
    capped: boolean
}): ReactNode => {
    const captionId = useId()
    const lines = keyed(rows.map((row) => row.join('\u0001')))
    const first = lines[0]?.key
    const more = moreRowsLabel(remaining, capped)

    return (
        <div
            data-testid="event-assistant-file-preview"
            tabIndex={0}
            aria-labelledby={captionId}
            className="overflow-x-auto rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-royal-20"
        >
            <table className="w-full text-left text-xs text-gray-900">
                <caption id={captionId} className="sr-only">{more ? `${label}. ${more}` : label}</caption>
                <tbody>
                    {lines.map((line) => (
                        <tr key={line.key} className={line.key === first ? undefined : 'border-t border-storm-80'}>
                            {keyed(line.value.split('\u0001')).map((cell) => (
                                <td
                                    key={cell.key}
                                    title={cell.value === '' ? undefined : cell.value}
                                    className="max-w-24 truncate px-1 py-0.5"
                                >
                                    {cell.value}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            {more && (
                <p aria-hidden="true" data-testid="event-assistant-file-more" className="mt-1 px-1 text-xs text-storm-30">{more}</p>
            )}
        </div>
    )
}

const shimmer = 'animate-shimmer bg-linear-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%]'

const STREAM_EVENTS = ['text_delta', 'reasoning_delta', 'tool_result', 'tool_approval_request', 'stream_end', 'stream_failed'] as const

const listen = (
    channelName: string,
    onEvent: (event: AssistantStreamEvent) => void,
    onSubscribed: () => void,
): (() => void) => {
    const service = PusherService.getInstance()
    const channel = service.subscribe(channelName)
    const handler = (data: AssistantStreamEvent): void => {
        onEvent(data)
    }

    channel.bind('pusher:subscription_succeeded', onSubscribed)
    STREAM_EVENTS.forEach((name) => {
        channel.bind(name, handler)
    })

    return () => {
        channel.unbind('pusher:subscription_succeeded', onSubscribed)
        STREAM_EVENTS.forEach((name) => {
            channel.unbind(name, handler)
        })
        service.unsubscribe(channelName)
    }
}

const waitToSend = (
    channelName: string,
    onEvent: (event: AssistantStreamEvent) => void,
): Promise<() => void> => new Promise((resolve) => {
    const holder: { stop: () => void } = {stop: () => undefined}
    const timer = window.setTimeout(() => resolve(() => holder.stop()), 500)

    holder.stop = listen(channelName, onEvent, () => {
        window.clearTimeout(timer)
        resolve(() => holder.stop())
    })
})

const failureMessage = (error: unknown): string => {
    if (!axios.isAxiosError(error)) {
        return 'The assistant could not answer.'
    }

    const message = (error.response?.data as {message?: unknown} | undefined)?.message

    return typeof message === 'string' && message !== ''
        ? message
        : 'The assistant could not answer.'
}

const settleDecision = (message: ChatLine, ids: Set<string>): ChatLine => {
    if (message.role !== 'assistant' || !ids.has(message.id)) {
        return message
    }

    if (!message.approvals.some((approval) => approval.tool === 'CoachImport' || approval.tool === 'resend_sale' || approval.tool === 'AddCoachPass' || approval.tool === 'CreateCoach' || approval.tool === 'RefundPurchase' || approval.tool === 'create_sale' || approval.tool === 'create_ticket' || approval.tool === 'create_event' || approval.tool === 'update_event' || approval.tool === 'create_ticket_type' || approval.tool === 'create_short_url' || approval.tool === 'update_ticket' || approval.tool === 'create_validation')) {
        return {...message, approvals: []}
    }

    return {
        ...message,
        approvals: message.approvals.map((approval) => ({...approval, settled: true})),
    }
}

const figureKey = (seen: Map<string, number>, seed: string): string => {
    const next = (seen.get(seed) ?? 0) + 1
    seen.set(seed, next)

    return next === 1 ? seed : `${seed} ${next}`
}

const FigureBlock = ({tools}: {tools: AssistantToolLine[]}): ReactNode => {
    const report = figureReport(tools)

    if (report === null) {
        return null
    }

    const seen = new Map<string, number>()

    if (report.lines.length > 0) {
        return (
            <div data-testid="event-assistant-figures" className="mt-2 flex flex-col gap-1">
                {report.lines.map((line) => (
                    <p key={figureKey(seen, line)} data-testid="event-assistant-figure-line" className="text-sm">{line}</p>
                ))}
            </div>
        )
    }

    return (
        <div data-testid="event-assistant-figures" className="mt-2 flex flex-col gap-3">
            {report.blocks.map((block) => (
                <FigureGroup
                    key={figureKey(seen, `${block.title}\n${block.note}\n${block.rows.map((row) => `${row.label}=${row.value}`).join('\n')}`)}
                    block={block}
                />
            ))}
        </div>
    )
}

const FigureGroup = ({block}: {block: FigureReport['blocks'][number]}): ReactNode => {
    const seen = new Map<string, number>()
    const headed = block.title !== '' || block.note !== ''

    return (
        <div data-testid="event-assistant-figure-group" data-group={block.title}>
            {block.title !== '' && <p className="text-xs font-semibold">{block.title}</p>}
            {block.note !== '' && <p className="text-xs text-storm-30">{block.note}</p>}
            <dl className={headed ? 'mt-1 flex flex-col gap-1' : 'flex flex-col gap-1'}>
                {block.rows.map((row) => (
                    <div key={figureKey(seen, `${row.label}=${row.value}`)} data-testid="event-assistant-figure" data-label={row.label} className="flex items-baseline justify-between gap-3">
                        <dt className="min-w-0 text-xs text-storm-30">{row.label}</dt>
                        <dd className="shrink-0 text-sm font-medium tabular-nums">{row.value}</dd>
                    </div>
                ))}
            </dl>
        </div>
    )
}

const PurchaseChoices = ({
    message,
    asking,
    pending,
    locked,
    refundsLocked,
    focused,
    replyRef,
    onSelect,
    onChoose,
}: {
    message: ChatLine
    asking: boolean
    pending: boolean
    locked: boolean
    refundsLocked: boolean
    focused: boolean
    replyRef: {current: HTMLParagraphElement | null}
    onSelect: (saleId: string, refund: boolean) => void
    onChoose: (steps: TicketNextSteps, step: TicketNextStep) => void
}): ReactNode => {
    const parts = purchaseReplyParts(message.text)
    const replyClass = focused
        ? 'whitespace-pre-wrap rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20'
        : 'whitespace-pre-wrap'

    return (
        <>
            {parts.lead !== '' && (
                <LinkedText
                    paragraphRef={focused ? replyRef : undefined}
                    tabIndex={focused ? -1 : undefined}
                    testId={message.done ? 'event-assistant-reply' : undefined}
                    hidden={!message.done}
                    className={replyClass}
                    text={parts.lead}
                />
            )}
            <ul data-testid="event-assistant-purchases" aria-label="Matching purchases" className="my-2 space-y-2">
                {message.purchases.map((purchase) => (
                    <li
                        key={purchase.saleId}
                        data-testid={`event-assistant-purchase-${purchase.saleId}`}
                        className="space-y-1 rounded-sm border border-storm-80 p-2"
                    >
                        <p className="text-xs font-medium leading-5">{purchase.purchaser}</p>
                        <p className="text-xs leading-5">{[purchase.when, purchase.tickets, purchase.total, purchase.uses].filter((part) => part !== '').join(' · ')}</p>
                        <p className="text-xs leading-5">{[purchase.email, purchase.mobile].filter((part) => part !== '').join(' · ')}</p>
                        <p className="text-xs leading-5">{[purchase.matched, purchase.holders, purchase.event, purchase.status].filter((part) => part !== '').join(' · ')}</p>
                        {purchase.steps.length > 0 ? (
                            <ol className="flex flex-col gap-2 pt-1">
                                {purchase.steps.map((step, index) => (
                                    <li key={step.id}>
                                        <button
                                            type="button"
                                            data-testid={`event-assistant-next-${purchase.saleId}-${step.id}`}
                                            disabled={asking || pending}
                                            onClick={() => onChoose({
                                                saleId: purchase.saleId,
                                                ticketId: purchase.ticketId,
                                                steps: purchase.steps,
                                            }, step)}
                                            className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-storm-80 bg-white px-3 py-2 text-left text-sm text-gray-900 hover:border-royal-20 hover:bg-storm-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 disabled:cursor-default"
                                        >
                                            <span className="w-4 font-semibold text-royal-10">{index + 1}</span>
                                            {step.label}
                                        </button>
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <GeneralButton
                                nativeType="button"
                                theme="primary"
                                className="w-full"
                                testId={purchase.action === 'refund' ? `event-assistant-refund-${purchase.saleId}` : `event-assistant-resend-${purchase.saleId}`}
                                disabled={asking || pending || (purchase.action === 'refund' ? refundsLocked : locked)}
                                onClick={() => {
                                    onSelect(purchase.saleId, purchase.action === 'refund')
                                }}
                            >
                                <>
                                    {purchase.action === 'refund' ? 'Refund' : 'Resend'}
                                    <span className="sr-only">{purchase.action === 'refund'
                                        ? ` sale for ${purchase.purchaser}, ${purchase.when}, ${purchase.tickets}, ${purchase.uses}`
                                        : ` tickets for ${purchase.purchaser}, ${purchase.when}, ${purchase.tickets}, ${purchase.uses}`}</span>
                                </>
                            </GeneralButton>
                        )}
                    </li>
                ))}
            </ul>
            {parts.follow !== '' && (
                <LinkedText
                    testId={message.done ? 'event-assistant-purchase-follow' : undefined}
                    hidden={!message.done}
                    className={replyClass}
                    text={parts.follow}
                />
            )}
        </>
    )
}

const NextSteps = ({
    steps,
    disabled,
    onChoose,
}: {
    steps: TicketNextSteps
    disabled: boolean
    onChoose: (step: TicketNextStep) => void
}): ReactNode => (
    <div data-testid="event-assistant-next-steps" role="group" aria-label="What would you like me to do?" className="mt-3">
        <p className="text-sm font-medium text-gray-900">What would you like me to do?</p>
        <ol className="mt-2 flex flex-col gap-2">
            {steps.steps.map((step, index) => (
                <li key={step.id}>
                    <button
                        type="button"
                        data-testid={`event-assistant-next-${step.id}`}
                        disabled={disabled}
                        onClick={() => onChoose(step)}
                        className="flex w-full cursor-pointer items-center gap-3 rounded-xl border border-storm-80 bg-white px-3 py-2.5 text-left text-sm text-gray-900 hover:border-royal-20 hover:bg-storm-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 disabled:cursor-default disabled:hover:border-storm-80 disabled:hover:bg-white"
                    >
                        <span className="w-4 font-semibold text-royal-10">{index + 1}</span>
                        {step.label}
                    </button>
                </li>
            ))}
        </ol>
    </div>
)

const EventAssistantPanel = ({
    eventId,
    subject = '',
    page,
    placement = 'event',
    concealed = false,
    onClose,
}: Props): ReactNode => {
    const [prompt, setPrompt] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [rows, setRows] = useState<string[][]>([])
    const [remaining, setRemaining] = useState(0)
    const [capped, setCapped] = useState(false)
    const [previewNote, setPreviewNote] = useState<string | null>(null)
    const [removalNote, setRemovalNote] = useState('')
    const [fileRefusal, setFileRefusal] = useState('')
    const [draggingFile, setDraggingFile] = useState(false)
    const [asking, setAsking] = useState(false)
    const [opening, setOpening] = useState(false)
    const [conversationId, setConversationId] = useState<string | null>(null)
    const [messages, setMessages] = useState<ChatLine[]>([])
    const [resendsLocked, setResendsLocked] = useState(false)
    const [refundsLocked, setRefundsLocked] = useState(false)
    const seenRef = useRef(new Set<string>())
    const unsubscribeRef = useRef<(() => void) | null>(null)
    const giveUpRef = useRef<number | null>(null)
    const busyRef = useRef(false)
    const decidingRef = useRef(false)
    const decisionIdsRef = useRef(new Set<string>())
    const fileRef = useRef<HTMLInputElement>(null)
    const attachRef = useRef<HTMLButtonElement>(null)
    const promptRef = useRef<HTMLTextAreaElement>(null)
    const firstApprovalRef = useRef<HTMLDivElement>(null)
    const wasAskingRef = useRef(false)
    const wasOpeningRef = useRef(false)
    const previewTicketRef = useRef(0)
    const dragDepthRef = useRef(0)
    const loadTokenRef = useRef(0)
    const logRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLElement>(null)
    const attachmentStatusId = useId()
    const replyRef = useRef<HTMLParagraphElement>(null)
    const busy = asking || opening
    const blank = conversationId === null && messages.length === 0
    const pending = messages.some((message) => (
        message.role === 'assistant' && message.approvals.some((approval) => approval.settled !== true)
    ))
    const canReceiveFile = eventId !== null && !busy && !pending
    const attachmentStatus = attachmentStatusText(file?.name ?? null, previewNote, rows.length, removalNote, remaining, capped, fileRefusal)
    const lastReplyId = messages.findLast((message) => (
        message.role === 'assistant' && message.done && message.text !== ''
    ))?.id

    const clearGiveUp = (): void => {
        if (giveUpRef.current !== null) {
            window.clearTimeout(giveUpRef.current)
            giveUpRef.current = null
        }
    }

    useEffect(() => () => {
        unsubscribeRef.current?.()
        if (giveUpRef.current !== null) {
            window.clearTimeout(giveUpRef.current)
        }
    }, [])

    useEffect(() => {
        promptRef.current?.focus({preventScroll: true})
    }, [])

    useEffect(() => {
        if (opening) {
            wasOpeningRef.current = true

            return
        }

        if (wasOpeningRef.current) {
            wasOpeningRef.current = false
            promptRef.current?.focus({preventScroll: true})
        }
    }, [opening])

    useEffect(() => {
        if (concealed) {
            return
        }

        const onKey = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape' || event.defaultPrevented || document.getElementById('event-assistant-recent-list')) {
                return
            }

            const active = document.activeElement

            if (active !== document.body && !panelRef.current?.contains(active)) {
                return
            }

            onClose()
            document.querySelector<HTMLElement>('[data-testid="event-assistant-toggle"]')?.focus({preventScroll: true})
        }

        window.addEventListener('keydown', onKey)

        return () => {
            window.removeEventListener('keydown', onKey)
        }
    }, [concealed, onClose])

    useLayoutEffect(() => {
        const field = promptRef.current

        if (!field) {
            return
        }

        field.style.height = ''

        if (field.scrollHeight > field.clientHeight) {
            field.style.height = `${Math.min(field.scrollHeight, 160)}px`
        }
    }, [prompt, concealed])

    useEffect(() => {
        if (asking) {
            wasAskingRef.current = true

            return
        }

        if (!wasAskingRef.current) {
            return
        }

        wasAskingRef.current = false

        const active = document.activeElement

        if (active !== document.body && active !== promptRef.current) {
            return
        }

        if (pending) {
            firstApprovalRef.current?.querySelector<HTMLButtonElement>('button')?.focus({preventScroll: true})

            return
        }

        if (active === document.body) {
            replyRef.current?.focus({preventScroll: true})
        }
    }, [asking, pending])

    const [fileEventId, setFileEventId] = useState(eventId)

    if (fileEventId !== eventId) {
        setFileEventId(eventId)

        if (eventId === null) {
            previewTicketRef.current += 1
            setFile(null)
            setRows([])
            setRemaining(0)
            setCapped(false)
            setPreviewNote(null)
            setFileRefusal('')
        }
    }

    useLayoutEffect(() => {
        const panel = panelRef.current

        if (!panel || placement === 'shell') {
            if (panel) {
                panel.style.top = ''
            }

            return
        }

        if (concealed) {
            return
        }

        const nav = document.querySelector('[aria-label="Tournament sections"]')

        if (!(nav instanceof HTMLElement)) {
            return
        }

        const place = (): void => {
            panel.style.top = `${Math.max(0, nav.getBoundingClientRect().bottom)}px`
        }

        place()
        window.addEventListener('scroll', place, true)
        window.addEventListener('resize', place)

        return () => {
            window.removeEventListener('scroll', place, true)
            window.removeEventListener('resize', place)
        }
    }, [placement, concealed])

    useEffect(() => {
        const log = logRef.current
        if (log) {
            log.scrollTop = log.scrollHeight
        }
    }, [messages])

    const apply = (event: AssistantStreamEvent): void => {
        const closingDecision = decidingRef.current && (event.type === 'stream_end' || event.type === 'stream_failed')

        if (event.type === 'stream_end' || event.type === 'stream_failed') {
            clearGiveUp()
            setAsking(false)

            if (closingDecision) {
                decidingRef.current = false
            }
        }

        setMessages((current) => {
            const base = closingDecision && event.type === 'stream_end'
                ? current.map((message) => settleDecision(message, decisionIdsRef.current))
                : current
            const next = [...base]
            const index = next.length - 1
            const last = next[index]

            if (!last || last.role !== 'assistant') {
                return base
            }

            const answer = applyAssistantEvents(last, [event], seenRef.current)
            next[index] = {...last, ...answer}

            return next
        })
    }

    const envelope = (channel: string): Record<string, string> => {
        const body: Record<string, string> = {
            surface: 'admin',
            route: route().current() ?? '',
            tab: eventId ? (page === '' ? 'overview' : page) : 'none',
            channel,
        }

        if (eventId) {
            body.event_id = eventId
        }

        return body
    }

    const storeUrl = (): string => eventId
        ? route('events.assistant.store', {event: eventId})
        : route('assistant.store')

    const converse = async (
        userText: string,
        payload: (channel: string) => FormData | Record<string, unknown>,
        deciding = false,
        attachment: {name: string, rows: string[][], remaining: number, capped: boolean} | null = null,
    ): Promise<void> => {
        if (busyRef.current) {
            return
        }

        busyRef.current = true
        const prior = messages
        const channel = `${window.pusherChannelAssistant}-${crypto.randomUUID()}`
        seenRef.current = new Set()
        unsubscribeRef.current?.()
        setAsking(true)

        if (deciding) {
            decidingRef.current = true
            decisionIdsRef.current = new Set(prior.flatMap((message) => message.role === 'assistant' && message.approvals.length > 0 ? [message.id] : []))
        }

        setMessages([
            ...(deciding ? prior : prior.map((message) => message.role === 'assistant' ? {...message, approvals: []} : message)),
            {
                id: crypto.randomUUID(),
                role: 'user',
                text: userText,
                thoughts: '',
                tools: [],
                purchases: [],
                nextSteps: null,
                approvals: [],
                failed: null,
                done: true,
                attachmentName: attachment?.name ?? null,
                rows: attachment?.rows ?? [],
                remaining: attachment?.remaining ?? 0,
                capped: attachment?.capped ?? false,
            },
            {id: crypto.randomUUID(), role: 'assistant', attachmentName: null, rows: [], remaining: 0, capped: false, ...emptyAnswer()},
        ])

        const stop = await waitToSend(channel, apply)
        unsubscribeRef.current = stop
        clearGiveUp()
        giveUpRef.current = window.setTimeout(() => {
            busyRef.current = false
            setAsking(false)
            setMessages((current) => {
                const next = [...current]
                const index = next.length - 1
                const last = next[index]

                if (!last || last.role !== 'assistant' || last.done) {
                    return current
                }

                next[index] = {...last, failed: 'The assistant did not answer.', done: true}

                return next
            })
        }, 45000)

        try {
            const response = await axios.post<{
                channel: string
                conversation_id: string
                events?: AssistantStreamEvent[]
            }>(storeUrl(), payload(channel))

            setConversationId(response.data.conversation_id)
            for (const event of response.data.events ?? []) {
                apply(event)
            }
        } catch (error) {
            clearGiveUp()
            setAsking(false)
            const message = failureMessage(error)

            if (deciding) {
                decidingRef.current = false
                setMessages(prior.map((line) => line.role === 'assistant' && line.approvals.length > 0
                    ? {...line, failed: message}
                    : line))

                return
            }

            setMessages((current) => {
                const next = [...current]
                const index = next.length - 1
                const last = next[index]

                if (!last || last.role !== 'assistant') {
                    return current
                }

                next[index] = {...last, failed: message, done: true}

                return next
            })
        } finally {
            busyRef.current = false
        }
    }

    const chooseFile = (next: File | null): void => {
        if (next && !eventId) {
            return
        }

        if (next) {
            const refusal = coachFileRefusal(next.name)

            if (refusal !== null) {
                setFileRefusal(refusal)

                return
            }
        }

        setFileRefusal('')

        const ticket = previewTicketRef.current + 1
        previewTicketRef.current = ticket
        setRemovalNote('')
        setFile(next)
        setRows([])
        setRemaining(0)
        setCapped(false)
        setPreviewNote(null)

        if (!next || !eventId) {
            return
        }

        const body = new FormData()
        body.append('file', next)
        setPreviewNote('Reading the file...')
        void axios.post<{rows?: unknown, remaining?: unknown, capped?: unknown}>(route('events.assistant.preview', {event: eventId}), body)
            .then((response) => {
                if (previewTicketRef.current !== ticket) {
                    return
                }

                const read = previewRows(response.data.rows)
                setRows(read)
                setRemaining(previewCount(response.data.remaining))
                setCapped(response.data.capped === true)
                setPreviewNote(read.length === 0 ? 'No rows to preview.' : null)
            })
            .catch(() => {
                if (previewTicketRef.current !== ticket) {
                    return
                }

                setPreviewNote('Could not read a preview.')
            })
    }

    const fileDrag = (event: React.DragEvent): boolean => Array.from(event.dataTransfer.types).includes('Files')

    // Moving across the paperclip and the prompt fires dragleave. The count keeps the overlay up.
    const showFileDrop = (event: React.DragEvent): void => {
        if (!canReceiveFile || !fileDrag(event)) {
            return
        }

        event.preventDefault()
        dragDepthRef.current += 1
        setDraggingFile(true)
    }

    const hideFileDrop = (event: React.DragEvent): void => {
        if (!fileDrag(event)) {
            return
        }

        event.preventDefault()
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)

        if (dragDepthRef.current === 0) {
            setDraggingFile(false)
        }
    }

    const allowFileDrop = (event: React.DragEvent): void => {
        if (eventId === null || !fileDrag(event)) {
            return
        }

        event.preventDefault()
        event.dataTransfer.dropEffect = canReceiveFile ? 'copy' : 'none'
    }

    const dropFile = (event: React.DragEvent): void => {
        if (eventId === null || !fileDrag(event)) {
            return
        }

        event.preventDefault()
        dragDepthRef.current = 0
        setDraggingFile(false)

        const item = event.dataTransfer.items?.[0]
        const dropped = event.dataTransfer.files?.[0]
            ?? (item?.kind === 'file' ? item.getAsFile() : null)
        const decision = composerDrop({
            acceptsFile: true,
            canReceive: canReceiveFile,
            names: dropped === null ? [] : [dropped.name],
        })

        if (decision.action === 'refuse') {
            setFileRefusal(decision.message)

            return
        }

        if (decision.action === 'attach' && dropped !== null) {
            chooseFile(dropped)
        }
    }

    const clearFile = (): void => {
        chooseFile(null)
        if (fileRef.current) {
            fileRef.current.value = ''
        }
    }

    const removeAttachedFile = (): void => {
        clearFile()
        setRemovalNote('Attached file removed.')
        attachRef.current?.focus({preventScroll: true})
    }

    const close = (): void => {
        onClose()
        window.requestAnimationFrame(() => {
            document.querySelector<HTMLElement>('[data-testid="event-assistant-toggle"]')?.focus({preventScroll: true})
        })
    }

    const resetDraft = (): void => {
        setPrompt('')
        clearFile()
        setResendsLocked(false)
        setRefundsLocked(false)
    }

    const threadLines = (thread: StoredThreadMessage[]): ChatLine[] => thread.map((message) => ({
        id: message.id,
        role: message.role,
        text: message.text,
        thoughts: '',
        tools: message.tools,
        purchases: message.purchases,
        nextSteps: message.nextSteps,
        approvals: message.approvals,
        failed: null,
        done: true,
        attachmentName: message.attachmentName,
        rows: [],
        remaining: 0,
        capped: false,
    }))

    const openConversation = async (id: string): Promise<boolean> => {
        if (busy) {
            return false
        }

        const token = loadTokenRef.current + 1
        loadTokenRef.current = token
        setOpening(true)
        setConversationId(null)
        setMessages([])
        resetDraft()

        try {
            const response = await axios.get<{messages?: unknown}>(route('assistant.conversations.show', placement === 'shell'
                ? {conversation: id, scope: 'organization'}
                : {conversation: id, event: eventId}))

            if (loadTokenRef.current !== token) {
                return false
            }

            setConversationId(id)
            setMessages(threadLines(storedThread(response.data.messages)))
            promptRef.current?.focus({preventScroll: true})

            return true
        } catch {
            if (loadTokenRef.current !== token) {
                return false
            }

            setMessages([{
                id: crypto.randomUUID(),
                role: 'assistant',
                attachmentName: null,
                rows: [],
                remaining: 0,
                capped: false,
                ...emptyAnswer(),
                failed: 'That chat could not be opened.',
                done: true,
            }])

            return false
        } finally {
            if (loadTokenRef.current === token) {
                setOpening(false)
            }
        }
    }

    const startNewChat = (): void => {
        if (busy || blank) {
            return
        }

        loadTokenRef.current += 1
        setOpening(false)
        setConversationId(null)
        setMessages([])
        resetDraft()
        promptRef.current?.focus({preventScroll: true})
    }

    const send = async (preset?: string): Promise<void> => {
        const text = (preset ?? prompt).trim()

        if ((text === '' && !file) || busy || pending) {
            return
        }

        const selected = file
        const attachedRows = rows
        const attachedRemaining = remaining
        const attachedCapped = capped
        setPrompt('')
        clearFile()
        promptRef.current?.focus({preventScroll: true})

        await converse(text, (channel) => {
            if (!selected) {
                return {
                    prompt: text,
                    conversation_id: conversationId,
                    ...envelope(channel),
                }
            }

            const body = new FormData()
            if (text !== '') {
                body.append('prompt', text)
            }
            if (conversationId) {
                body.append('conversation_id', conversationId)
            }
            Object.entries(envelope(channel)).forEach(([key, value]) => {
                body.append(key, value)
            })
            body.append('file', selected)

            return body
        }, false, selected ? {name: selected.name, rows: attachedRows, remaining: attachedRemaining, capped: attachedCapped} : null)
    }

    const chooseNext = async (steps: TicketNextSteps, step: TicketNextStep): Promise<void> => {
        if (busy || pending || busyRef.current) {
            return
        }

        await converse(step.prompt, (channel) => ({
            prompt: step.prompt,
            sale_id: steps.saleId,
            ...(step.id === 'refund' ? {refund: true} : {}),
            ...(step.id === 'extend' || step.id === 'use' || step.id === 'link' ? {step: step.id} : {}),
            ...(step.id === 'use' && steps.ticketId ? {ticket_id: steps.ticketId} : {}),
            conversation_id: conversationId,
            ...envelope(channel),
        }))
    }

    const selectPurchase = async (saleId: string, refund: boolean): Promise<void> => {
        if (busy || pending || busyRef.current || (refund ? refundsLocked : resendsLocked)) {
            return
        }

        if (refund) {
            setRefundsLocked(true)
        } else {
            setResendsLocked(true)
        }

        const text = refund ? 'Refund this purchase.' : 'Resend this purchase.'

        await converse(text, (channel) => ({
            prompt: text,
            sale_id: saleId,
            ...(refund ? {refund: true} : {}),
            conversation_id: conversationId,
            ...envelope(channel),
        }))
    }

    const decide = async (id: string, action: 'approve' | 'reject', sendLater = false, percentage?: number): Promise<void> => {
        if (!conversationId || busy || busyRef.current) {
            return
        }

        const approvals = messages.flatMap((message) => message.role === 'assistant' ? message.approvals : [])
        const chosen = approvals.find((approval) => approval.id === id)
        const line = decisionLine(chosen?.tool, action, sendLater, percentage, chosen?.reason)

        await converse(line, (channel) => ({
            decisions: approvalDecisions(approvals, id, action, sendLater, percentage),
            conversation_id: conversationId,
            ...envelope(channel),
        }), true)
    }

    return (
        <aside
            ref={panelRef}
            id="event-assistant-panel"
            data-testid="event-assistant-panel"
            aria-labelledby="event-assistant-title"
            hidden={concealed}
            className={placement === 'shell'
                ? 'fixed inset-y-0 right-0 z-30 flex w-full flex-col overflow-hidden border-storm-60 bg-white shadow-xl motion-safe:animate-assistant-in lg:w-[24rem] lg:border-l lg:shadow-[-4px_0_16px_rgba(31,37,48,0.06)] 2xl:w-[28rem]'
                : 'fixed right-0 bottom-0 z-20 flex w-full max-w-full flex-col overflow-hidden border-l border-storm-60 bg-white shadow-xl motion-safe:animate-assistant-in sm:w-80'}
        >
            <div className="relative flex items-center gap-1 border-b border-storm-80 py-2 pr-2 pl-4">
                <span aria-hidden="true" className="mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-royal-10 text-white">
                    <SparklesIcon className="size-4"/>
                </span>
                <div className="min-w-0 flex-1">
                    <h2 id="event-assistant-title" className="text-sm! leading-5! font-semibold! tracking-normal! text-gray-900">Duesy AI</h2>
                    {subject !== '' && (
                        <p data-testid="event-assistant-subject" className="truncate text-xs text-storm-40" title={subject}>Asking about {subject}</p>
                    )}
                </div>
                <EventAssistantRecent
                    eventId={eventId}
                    organizationWide={placement === 'shell'}
                    busy={busy}
                    blank={blank}
                    currentId={conversationId}
                    onOpen={openConversation}
                    onNew={startNewChat}
                />
                <button
                    type="button"
                    title="Close"
                    className={iconButtonClass}
                    onClick={close}
                >
                    <span aria-hidden="true">
                        <XMarkIcon className="size-5"/>
                    </span>
                    <span className="sr-only">Close assistant</span>
                </button>
            </div>
            <div ref={logRef} role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversation" className="min-h-0 flex-1 space-y-3 overflow-y-auto px-[max(1rem,calc((100%_-_42rem)_/_2))] py-3">
                {messages.length === 0 && (
                    opening ? (
                        <p className="text-sm text-storm-40">Loading chat.</p>
                    ) : (
                        <div className="flex min-h-full flex-col justify-end gap-3">
                            <div>
                                <p className="text-base font-semibold text-gray-900">How can I help?</p>
                                <p className="mt-0.5 text-sm text-storm-40">{eventId
                                    ? 'Ask about this tournament or the organization.'
                                    : 'Ask about the organization.'}</p>
                            </div>
                            {prompt.trim() === '' && file === null && (
                                <div data-testid="event-assistant-starters" role="group" aria-label="Suggested questions" className="flex flex-col items-start gap-2">
                                    {starterQuestions.filter((question) => eventId !== null || question.id !== 'coach-passes').map((question) => (
                                        <button
                                            key={question.id}
                                            type="button"
                                            data-testid={`event-assistant-starter-${question.id}`}
                                            onClick={() => {
                                                void send(question.label)
                                            }}
                                            className="cursor-pointer rounded-2xl border border-royal-40 bg-royal-60 px-3.5 py-2 text-left text-sm font-medium text-royal-10 hover:border-royal-20 hover:bg-storm-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 focus-visible:ring-offset-2"
                                        >
                                            {question.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                )}
                {messages.filter(showsTranscriptBubble).map((message) => (
                    <div key={message.id} className={message.role === 'user' ? 'text-right' : 'text-left'}>
                        <div
                            data-testid="event-assistant-message"
                            className={message.role === 'user'
                                ? 'inline-block max-w-[85%] rounded-2xl bg-royal-50 px-3 py-2 text-left text-sm text-gray-900'
                                : 'mr-6 block rounded-2xl border border-storm-80 bg-white px-3 py-2 text-sm text-gray-900'}
                        >
                            {message.role === 'assistant' && message.thoughts !== '' && (
                                <div hidden data-testid="event-assistant-thoughts">{message.thoughts}</div>
                            )}
                            {message.role === 'assistant' && message.tools.map((tool) => (
                                <p key={tool.id} hidden data-testid="event-assistant-tool-result">{tool.text}</p>
                            ))}
                            {message.role === 'assistant' && message.purchases.length > 0 && (
                                <PurchaseChoices
                                    message={message}
                                    asking={busy}
                                    pending={pending}
                                    locked={resendsLocked}
                                    refundsLocked={refundsLocked}
                                    focused={message.id === lastReplyId}
                                    replyRef={replyRef}
                                    onSelect={(saleId, refund) => {
                                        void selectPurchase(saleId, refund)
                                    }}
                                    onChoose={(steps, step) => {
                                        void chooseNext(steps, step)
                                    }}
                                />
                            )}
                            {message.role === 'assistant' && message.approvals.map((approval, index) => (
                                <EventAssistantApproval
                                    key={approval.id}
                                    approval={approval}
                                    index={index}
                                    asking={busy}
                                    firstRef={index === 0 ? firstApprovalRef : undefined}
                                    decide={decide}
                                />
                            ))}
                            {message.role === 'assistant' && message.text === '' && !message.done && message.approvals.length === 0 && (
                                <div data-testid="event-assistant-status" aria-busy="true" className="space-y-2 py-1">
                                    <p className="text-xs font-medium text-storm-40">The assistant is replying.</p>
                                    <div aria-hidden="true" className={`h-3 rounded ${shimmer}`}/>
                                    <div aria-hidden="true" className={`h-3 w-4/5 rounded ${shimmer}`}/>
                                    <div aria-hidden="true" className={`h-3 w-2/3 rounded ${shimmer}`}/>
                                </div>
                            )}
                            {message.role === 'user' && message.attachmentName && (
                                <>
                                    <span className="sr-only">Attached file: </span>
                                    <p data-testid="event-assistant-sent-file" className="text-xs font-medium" title={message.attachmentName}>{message.attachmentName}</p>
                                </>
                            )}
                            {message.role === 'user' && message.rows.length > 0 && (
                                <div className={message.attachmentName ? 'mt-2' : undefined}>
                                    <FilePreview
                                        rows={message.rows}
                                        remaining={message.remaining}
                                        capped={message.capped}
                                        label={message.attachmentName ? `Rows from ${message.attachmentName}` : 'Rows from the attached file'}
                                    />
                                </div>
                            )}
                            {message.role === 'assistant' && message.purchases.length === 0 && message.text !== '' && !message.done && (
                                // Growing text stays out of the log so the finished reply is announced once.
                                <p aria-hidden="true" className="whitespace-pre-wrap">{message.text}</p>
                            )}
                            {message.purchases.length === 0 && message.text !== '' && (message.role === 'user' || message.done) && (
                                <LinkedText
                                    paragraphRef={message.id === lastReplyId ? replyRef : undefined}
                                    tabIndex={message.id === lastReplyId ? -1 : undefined}
                                    testId={message.role === 'assistant' ? 'event-assistant-reply' : undefined}
                                    className={message.id === lastReplyId
                                        ? 'whitespace-pre-wrap rounded-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20'
                                        : 'whitespace-pre-wrap'}
                                    text={message.text}
                                />
                            )}
                            {message.role === 'assistant' && message.purchases.length === 0 && message.done && message.nextSteps && (
                                <NextSteps
                                    steps={message.nextSteps}
                                    disabled={busy || pending}
                                    onChoose={(step) => {
                                        const chosen = message.nextSteps

                                        if (chosen) {
                                            void chooseNext(chosen, step)
                                        }
                                    }}
                                />
                            )}
                            {message.role === 'assistant' && message.purchases.length === 0 && (
                                <FigureBlock tools={message.tools}/>
                            )}
                            {message.failed && (
                                <p role="alert" data-testid="event-assistant-error" className="text-red-700">{message.failed}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <form
                aria-label="Ask Duesy AI"
                className="flex flex-col gap-2 border-t border-storm-80 px-[max(1rem,calc((100%_-_42rem)_/_2))] py-3"
                onSubmit={(event) => {
                    event.preventDefault()
                    void send()
                }}
            >
                <div id={attachmentStatusId} role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                    {attachmentStatus}
                </div>
                <div
                    data-testid="event-assistant-composer"
                    className={`relative flex flex-col gap-1 rounded-xl border bg-white px-1 py-1 ${draggingFile
                        ? 'border-royal-10 ring-2 ring-royal-20'
                        : 'border-storm-50 focus-within:border-royal-20 focus-within:ring-2 focus-within:ring-royal-20'}`}
                    onDragEnter={eventId ? showFileDrop : undefined}
                    onDragOver={eventId ? allowFileDrop : undefined}
                    onDragLeave={eventId ? hideFileDrop : undefined}
                    onDrop={eventId ? dropFile : undefined}
                >
                    {draggingFile && canReceiveFile && (
                        <div
                            aria-hidden="true"
                            data-testid="event-assistant-drop"
                            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-royal-10 bg-royal-50"
                        >
                            <span className="text-sm font-semibold text-storm-10">Drop Here</span>
                        </div>
                    )}
                    {file && (
                        <div className="px-1.5 pt-2">
                            <div
                                data-testid="event-assistant-attachment"
                                aria-busy={previewNote === 'Reading the file...' || undefined}
                                className="relative flex min-h-28 w-28 flex-col gap-1 rounded-xl border border-storm-50 bg-storm-100 p-2"
                            >
                                <p
                                    className={`${previewNote ? 'line-clamp-2' : 'line-clamp-3'} break-all pr-2 text-xs font-medium text-gray-900`}
                                    title={file.name}
                                >
                                    {file.name}
                                </p>
                                {previewNote && (
                                    <p aria-hidden="true" className="line-clamp-2 text-[10px] text-storm-30">{previewNote}</p>
                                )}
                                <span
                                    aria-hidden="true"
                                    data-testid="event-assistant-file-type"
                                    className="mt-auto self-start rounded-sm bg-storm-90 px-1.5 py-0.5 text-[10px] font-semibold text-storm-30"
                                >
                                    {fileTypeBadge(file.name)}
                                </span>
                                <button
                                    type="button"
                                    aria-label="Remove attached file"
                                    data-testid="event-assistant-remove-file"
                                    disabled={busy || pending}
                                    onClick={removeAttachedFile}
                                    className="absolute -top-2 -right-2 inline-flex size-6 cursor-pointer items-center justify-center rounded-full border border-storm-50 bg-white text-storm-40 shadow-xs hover:text-storm-30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 disabled:cursor-no-drop disabled:opacity-50"
                                >
                                    <span aria-hidden="true">
                                        <XMarkIcon className="size-3.5"/>
                                    </span>
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="flex items-start gap-1">
                        {eventId && <button
                            ref={attachRef}
                            type="button"
                            aria-label="Attach a coach file"
                            aria-describedby={attachmentStatusId}
                            data-testid="event-assistant-attach"
                            disabled={busy || pending}
                            onClick={() => fileRef.current?.click()}
                            className="inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-storm-40 hover:bg-storm-90 hover:text-storm-30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-royal-20 disabled:opacity-50"
                        >
                            <span aria-hidden="true">
                                <PaperclipIcon className="size-5"/>
                            </span>
                        </button>}
                        {eventId && <label htmlFor="event-assistant-file" className="sr-only">Coach file</label>}
                        {eventId && <input
                            ref={fileRef}
                            id="event-assistant-file"
                            data-testid="event-assistant-file"
                            type="file"
                            accept={COACH_FILE_ACCEPT}
                            disabled={busy || pending}
                            tabIndex={-1}
                            onChange={(event) => {
                                chooseFile(event.target.files?.[0] ?? null)
                                event.target.value = ''
                            }}
                            className="sr-only"
                        />}
                        <label className="min-w-0 flex-1" htmlFor="event-assistant-prompt">
                            <span className="sr-only">{eventId ? 'Ask about this tournament' : 'Ask about the organization'}</span>
                            <textarea
                                ref={promptRef}
                                id="event-assistant-prompt"
                                data-testid="event-assistant-prompt"
                                value={prompt}
                                rows={1}
                                placeholder={eventId ? 'Ask about this tournament' : 'Ask about the organization'}
                                disabled={opening || pending}
                                onChange={(event) => setPrompt(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) {
                                        return
                                    }

                                    event.preventDefault()

                                    if (prompt.trim() !== '' || file) {
                                        void send()
                                    }
                                }}
                                className="block w-full resize-none border-0 bg-transparent px-1 py-1.5 text-base/6 text-gray-900 sm:text-sm/6 placeholder:text-storm-40 focus:ring-0 focus:outline-hidden"
                            />
                        </label>
                        <button
                            type="submit"
                            aria-label="Send"
                            data-testid="event-assistant-send"
                            disabled={busy || pending || (prompt.trim() === '' && !file)}
                            className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg bg-royal-10 text-storm-90 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white enabled:hover:brightness-95 disabled:cursor-no-drop disabled:bg-transparent disabled:text-storm-50"
                        >
                            <span aria-hidden="true">
                                <ArrowUpIcon className="size-4"/>
                            </span>
                        </button>
                    </div>
                </div>
                {fileRefusal !== '' && (
                    <p role="alert" data-testid="event-assistant-file-error" className="text-xs text-red-700">{fileRefusal}</p>
                )}
            </form>
        </aside>
    )
}

export default EventAssistantPanel
